import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Chapter } from "../components/Chapter";
import { Reveal, MaskedLine } from "../lib/motion";
import { usePageSeo } from "../lib/i18nContext";
import LocalizedLink from "../components/LocalizedLink";
import EnquiryForm from "../components/EnquiryForm";
import { StructuredLines } from "../components/Motifs";
import { HeroFlow } from "../components/HeroFlow";

// Artwork layers: full composition on wide desktop, a bottom-right crop below the copy on smaller screens.
const fade = (px, top = 0) =>
  ({ WebkitMaskImage: `linear-gradient(to bottom, ${top ? `transparent 0, #000 ${top}px, ` : ""}#000 calc(100% - ${px}px), transparent 100%)`, maskImage: `linear-gradient(to bottom, ${top ? `transparent 0, #000 ${top}px, ` : ""}#000 calc(100% - ${px}px), transparent 100%)` });

// Wide desktop: the traced artwork is placed so its low waves (units y≈600) sit just below the CTA row,
// keeping a clear zone around the copy in every language.
const useArtLayout = (sectionRef, ctaRef) => {
  const [layout, setLayout] = useState(null);
  useEffect(() => {
    const section = sectionRef.current;
    const cta = ctaRef.current;
    if (!section || !cta) return undefined;
    const measure = () => {
      const w = section.clientWidth;
      if (w < 1280) {
        setLayout(null);
        return;
      }
      const s = w / 1600;
      const ctaBottom = cta.getBoundingClientRect().bottom - section.getBoundingClientRect().top;
      const top = Math.round(ctaBottom + 36 - 600 * s);
      setLayout({ top, height: Math.round(800 * s) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(section);
    return () => ro.disconnect();
  }, [sectionRef, ctaRef]);
  return layout;
};

const HeroArt = ({ layout }) => (
  <>
    {layout && (
      <div className="absolute inset-x-0 pointer-events-none rtl:-scale-x-100" style={{ top: layout.top, height: layout.height, ...fade(100, 140) }}>
        <HeroFlow />
      </div>
    )}
    <div className="hidden md:block xl:hidden absolute inset-x-0 bottom-0 h-[230px] pointer-events-none rtl:-scale-x-100" style={fade(80, 60)}>
      <HeroFlow band />
    </div>
    <div className="md:hidden absolute inset-x-0 bottom-0 h-[150px] pointer-events-none rtl:-scale-x-100" style={fade(60, 40)}>
      <HeroFlow band travellers={1} />
    </div>
  </>
);
import { CategoryTile } from "../components/CategoryTile";

const WRAP = "max-w-[1240px] mx-auto px-6 md:px-12 xl:px-16";
const H2 = "font-display text-[26px] md:text-[32px] font-semibold tracking-tight text-ink leading-tight";
const TEXT_LINK = "inline-flex items-center gap-2 text-sm font-semibold text-ink underline decoration-pine decoration-2 underline-offset-[6px] hover:text-pine transition-colors";

const Hero = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const ctaRef = useRef(null);
  const layout = useArtLayout(sectionRef, ctaRef);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative bg-white pt-nav overflow-hidden"
      style={layout ? { minHeight: layout.top + layout.height } : undefined}
    >
      <HeroArt layout={layout} />
      <div className={`${WRAP} relative z-10 pt-16 md:pt-24 lg:pt-32 pb-[150px] md:pb-[230px] xl:pb-[120px]`}>
        <div className="max-w-[860px]">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="font-mono text-xs md:text-[13px] font-medium tracking-[0.18em] uppercase text-pine"
            data-testid="hero-eyebrow"
          >
            [ 01 ] {t("footer.positioning")}
          </motion.p>
          <h1 className="font-display font-bold text-ink text-[40px] sm:text-[48px] lg:text-[64px] tracking-[-0.025em] leading-[1.08] mt-8 md:mt-10" data-testid="hero-headline">
            <MaskedLine delay={0.2}>{t("home.hero.line1")}</MaskedLine>
            <MaskedLine delay={0.3}>{t("home.hero.line2")}</MaskedLine>
            <MaskedLine delay={0.4}>{t("home.hero.line3")}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4, ease: "easeOut" }}
            className="text-slate text-base md:text-lg leading-relaxed max-w-2xl mt-8"
            data-testid="hero-subheading"
          >
            {t("home.hero.sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.72, duration: 0.4, ease: "easeOut" }}
            ref={ctaRef}
            className="flex flex-wrap items-center gap-x-8 gap-y-5 mt-10"
          >
            <LocalizedLink
              to="/contact"
              data-testid="hero-send-requirement-cta"
              className="inline-flex items-center gap-2 bg-ink text-white font-semibold px-7 py-3.5 rounded text-sm transition-colors duration-300 hover:bg-deep"
            >
              {t("home.hero.cta1")} <ArrowRight size={16} className="rtl:-scale-x-100" />
            </LocalizedLink>
            <LocalizedLink to="/products" data-testid="hero-explore-products-cta" className={TEXT_LINK}>
              {t("home.hero.cta2")} <ArrowRight size={16} className="rtl:-scale-x-100" />
            </LocalizedLink>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const AboutStrip = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="home-about-section" className="bg-white border-b border-ice">
      <div className={`${WRAP} py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8`}>
        <Reveal className="lg:col-span-3">
          <p className="font-mono text-xs md:text-[13px] font-medium tracking-[0.18em] uppercase text-pine">{t("home.about.label")}</p>
        </Reveal>
        <div className="lg:col-span-9 max-w-3xl">
          <Reveal delay={0.06}>
            <p className="text-ink/85 leading-relaxed text-base md:text-lg">{t("home.about.p1")}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <LocalizedLink to="/about" data-testid="about-teaser-cta" className={`${TEXT_LINK} mt-8`}>
              {t("home.about.cta")} <ArrowRight size={15} className="rtl:-scale-x-100" />
            </LocalizedLink>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ProductsSection = () => {
  const { t } = useTranslation();
  const categories = t("categories.items", { returnObjects: true });
  return (
    <section data-testid="home-products-section" className="py-16 md:py-28 bg-white">
      <div className={WRAP}>
        <Chapter index="02" label={t("home.products.chapter")} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8 mb-14 md:mb-20">
          <Reveal className="lg:col-span-7">
            <h2 className={H2}>{t("home.products.heading")}</h2>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5 flex items-end">
            <p className="text-slate leading-relaxed">{t("home.products.intro")}</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-s border-ice">
          {(Array.isArray(categories) ? categories : []).map((c, i) => (
            <CategoryTile key={c.id} c={c} i={i} meta={t("home.products.meta")} explore={t("home.products.explore")} />
          ))}
        </div>
      </div>
    </section>
  );
};

const HowSection = () => {
  const { t } = useTranslation();
  const steps = t("steps.items", { returnObjects: true });
  return (
    <section data-testid="home-how-section" className="py-16 md:py-28 bg-ice">
      <div className={WRAP}>
        <Chapter index="03" label={t("home.how.chapter")} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8 mb-14 md:mb-20">
          <Reveal className="lg:col-span-7">
            <h2 className={H2}>{t("home.how.heading")}</h2>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5 flex items-end">
            <p className="text-ink/80 leading-relaxed">{t("home.how.intro")}</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 border-s md:border-s-0 md:border-t border-ink/15">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div className="relative ps-6 md:ps-0 md:pt-8 pb-8 md:pb-0 md:pe-6" data-testid={`home-step-0${i + 1}`}>
                <span className="absolute start-0 md:start-auto md:top-[-1px] top-0 md:h-px md:w-8 h-8 w-px md:border-t-2 border-s-2 md:border-s-0 border-pine" aria-hidden="true" />
                <p className="font-mono text-xs font-medium tracking-[0.18em] text-pine">0{i + 1}</p>
                <h3 className="font-display text-lg font-semibold text-ink mt-4">{s.title}</h3>
                <p className="text-sm text-ink/80 mt-2 leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.08}>
          <LocalizedLink to="/how-we-work" data-testid="how-section-cta" className={`${TEXT_LINK} mt-14`}>
            {t("home.how.cta")} <ArrowRight size={16} className="rtl:-scale-x-100" />
          </LocalizedLink>
        </Reveal>
      </div>
    </section>
  );
};

const QualitySection = () => {
  const { t } = useTranslation();
  const docs = t("docs.items", { returnObjects: true });
  return (
    <section data-testid="home-quality-section" className="on-navy py-16 md:py-28 bg-ink text-white relative overflow-hidden">
      <StructuredLines tone="dark" cols={4} rows={3} className="absolute top-16 end-6 md:end-12 xl:end-16 w-40 hidden lg:block" />
      <div className={`${WRAP} relative`}>
        <Chapter index="04" label={t("home.quality.chapter")} dark />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8 mb-14">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-[26px] md:text-[32px] font-semibold tracking-tight leading-tight">{t("home.quality.heading")}</h2>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5 flex items-end">
            <p className="text-ice leading-relaxed">{t("home.quality.intro")}</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 border-t border-white/15">
          {docs.map((d, i) => (
            <Reveal key={d} delay={(i % 2) * 0.05}>
              <div className="flex items-baseline gap-5 py-4 border-b border-white/15" data-testid={`home-doc-${i}`}>
                <span className="font-mono text-xs text-ice w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm md:text-base text-white">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mt-10">
          <p className="font-mono text-xs tracking-[0.16em] uppercase text-ice">{t("home.quality.whereApplicable")}</p>
          <LocalizedLink to="/quality" data-testid="quality-teaser-cta" className="inline-flex items-center gap-2 text-sm font-semibold text-white underline decoration-ice/60 decoration-2 underline-offset-[6px] hover:decoration-white transition-colors">
            {t("home.quality.cta")} <ArrowRight size={16} className="rtl:-scale-x-100" />
          </LocalizedLink>
        </div>
      </div>
    </section>
  );
};

const WhySection = () => {
  const { t } = useTranslation();
  const items = t("home.why.items", { returnObjects: true });
  return (
    <section data-testid="home-why-section" className="py-16 md:py-28 bg-white">
      <div className={WRAP}>
        <Chapter index="05" label={t("home.why.chapter")} />
        <Reveal>
          <h2 className={`${H2} mt-8 max-w-3xl`} data-testid="why-heading">{t("home.why.heading")}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-s border-ice mt-14 md:mt-20">
          {items.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.06}>
              <div className="border-e border-b border-ice p-8 md:p-10 h-full" data-testid={`why-card-0${i + 1}`}>
                <p className="font-mono text-xs font-medium tracking-[0.18em] text-pine">0{i + 1}</p>
                <h3 className="font-display text-xl font-semibold text-ink mt-6">{w.title}</h3>
                <p className="text-sm text-slate mt-3 leading-relaxed">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const EnquirySection = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="enquiry-cta-section" className="py-16 md:py-28 bg-mist border-t border-ice">
      <div className={WRAP}>
        <Chapter index="06" label={t("home.cta.chapter")} />
        <h2 className="font-display font-semibold text-ink text-[30px] md:text-[40px] tracking-tight leading-[1.15] mt-8 mb-14 max-w-3xl" data-testid="enquiry-cta-heading">
          <MaskedLine delay={0.1}>{t("home.cta.line1")}</MaskedLine>
          <MaskedLine delay={0.2}>{t("home.cta.line2")}</MaskedLine>
          <MaskedLine delay={0.3}>{t("home.cta.line3")}</MaskedLine>
        </h2>
        <EnquiryForm />
      </div>
    </section>
  );
};

const Home = () => {
  usePageSeo("home");
  return (
    <main>
      <Hero />
      <AboutStrip />
      <ProductsSection />
      <HowSection />
      <QualitySection />
      <WhySection />
      <EnquirySection />
    </main>
  );
};

export default Home;
